import React, { createContext, useState, useContext, useMemo, useEffect } from 'react'
import { useRouter } from 'next/router'
import { client } from '../lib/apollo'
import { hasModuleAccess, extractModuleNames, MODULES } from '../utils/modules'
import { fetchCompany } from '../utils/api'
import { toast } from '../hooks/use-toast'

/**
 * @typedef {Object} AuthContextValue
 * @property {boolean} isAuthenticated
 * @property {any} user
 * @property {(payload: { user: any, token: string }) => void} login
 * @property {() => void} logout
 * @property {(companyId: any) => void} changeCurrentCompany
 * @property {(moduleName: string) => boolean} hasModule
 */

/** @type {React.Context<AuthContextValue>} */
const AuthContext = createContext({
  isAuthenticated: false, user: {}, login: () => { }, logout: () => { }, changeCurrentCompany: () => { }, hasModule: () => false
});

export const AuthProvider = ({ children }) => {
  let localStorage;
  if (typeof window !== 'undefined') {
    localStorage = window.localStorage
  }

  let initialState = localStorage && JSON.parse(localStorage.getItem("userInfo")) ? JSON.parse(localStorage.getItem("userInfo")) : {}
  const [user, setUser] = useState(initialState)

  const login = ({ user, token }) => {
    client.clearStore().catch(() => { });
    setUser({ user: user, token: token })
    localStorage.setItem("userInfo", JSON.stringify({ user: user, token: token }))
  }

  const router = useRouter()
  const logout = () => {
    localStorage.removeItem("userInfo")
    setUser({})
    router.push("/login")
  }

  const changeCurrentCompany = (companyId) => {
    setUser(prev => {
      return {
        ...prev,
        user: { ...prev.user, company_id: companyId }
      }
    })
  }

  const isAuthenticated = Object.keys(user).length > 0;

  // Super admins aren't scoped to a single company's subscription, so they bypass module gating entirely.
  const hasModule = (moduleName) => {
    if (user?.user?.role === 'super_admin') return true;
    return hasModuleAccess(user?.user?.modules, moduleName);
  };

  // Re-fetch the company's current module access on page load/reload (not on
  // in-app navigation, since this only runs once per AuthProvider mount) so a
  // subscription change takes effect without waiting for the next login.
  useEffect(() => {
    const currentUser = initialState?.user;
    if (!currentUser || currentUser.type !== 'company' || currentUser.role === 'super_admin') {
      return;
    }
    // Only the employee/dispatcher login type requires 'dispatcher mode' — every other
    // company-side role (owner, admin, ...) only needs 'dispatch'. Mirrors pages/login.js.
    const isDispatcherSession = currentUser.role === 'employee';
    const companyId = currentUser.company_id || currentUser.id;
    if (!companyId) return;

    fetchCompany(companyId, initialState?.token).then((companyData) => {
      if (!companyData) return;
      const modules = extractModuleNames(companyData);

      if (!hasModuleAccess(modules, MODULES.DISPATCH)) {
        toast({
          variant: "destructive",
          title: "Access revoked",
          description: "Your company no longer has access to Dispatch. You've been logged out.",
        });
        logout();
        return;
      }
      if (isDispatcherSession && !hasModuleAccess(modules, MODULES.DISPATCHER_MODE)) {
        toast({
          variant: "destructive",
          title: "Access revoked",
          description: "Dispatcher mode is no longer enabled for your company. You've been logged out.",
        });
        logout();
        return;
      }

      setUser((prev) => {
        const updated = {
          ...prev,
          user: {
            ...prev.user,
            modules,
            currency: companyData?.currency ?? prev.user?.currency,
            country: companyData?.company_country ?? prev.user?.country,
          },
        };
        localStorage.setItem("userInfo", JSON.stringify(updated));
        return updated;
      });
    });
    // Runs once, on mount (i.e. once per page load/reload).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = useMemo(() => ({
    isAuthenticated,
    user,
    login,
    logout,
    changeCurrentCompany,
    hasModule
  }), [isAuthenticated, user]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)